import { GroupedClassifications } from 'util/createFiltersQuery';
import { toFileExtension } from 'util/toFileExtension';
import {
  selectCommentsPageFilterQueryString,
  selectFiltersQuery,
  selectIsCompareMode,
  selectLanguage,
  selectLifecycleFiltersQuery,
  selectSorting,
  selectSummaryCategoryViewMode,
} from 'util/selector/querySelector';
import { selectReportCategoryId } from 'util/selector/reportSelector';

import { selectCurrentProjectId, selectIsLifecycleProjectKind } from 'reduxStore/project/selectors';
import FileSaver from 'file-saver';
import {
  selectCurrentCategoryId,
  selectDetailsByCategoryId,
} from 'reduxStore/engagementDrivers/selectors';
import { selectSelectedQuestionId } from 'reduxStore/comments/selectors';
import { queryDto } from 'api/dto/Query.dto';
import { AdvocacyDto } from 'api/dto/Advocacy.dto';
import { reportApi } from 'container/reportApi';
import { ReportFormat, ReportFormatType } from 'model/ReportFormat';
import { MonthlyBreakdownReportType } from 'model/MonthlyBreakdownReport';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { STORE_NAME } from 'reduxStore/overallSurvey/initialState';
import { serializeError } from 'shared/utils/redux';
import { ReportFileExtension } from 'model/ReportFileExtension';
import { ModeType } from 'register/ModeType';
import { QueryType } from 'model/QueryType';

import { getFeedbackCombinedReportQuery } from './getFeedbackCombinedReportQuery';

export const downloadFeedbackReport = createAsyncThunk<
  void,
  { type: ReportFormatType; isLifecycleProjectKind?: boolean }
>(
  `${STORE_NAME}/downloadFeedbackReport`,
  ({ type, isLifecycleProjectKind }, { getState }) => {
    const projectId = selectCurrentProjectId(getState());
    const format = toFileExtension(type);
    const mode = selectIsCompareMode(getState()) ? ModeType.COMPARED : ModeType.COMBINED;

    const filtersQuery = isLifecycleProjectKind
      ? selectLifecycleFiltersQuery(getState())
      : selectFiltersQuery(getState());

    const categoryMode = selectSummaryCategoryViewMode(getState());
    const language = selectLanguage(getState());
    const sortBy = selectSorting(getState());

    const query = queryDto.build(
      filtersQuery,
      queryDto.stringifyCategoryMode(categoryMode),
      queryDto.stringifyLanguage(language),
      queryDto.stringifySortBy(sortBy)
    );

    return reportApi
      .getFeedbackReport(projectId, query, mode, type)
      .then((data) => FileSaver.saveAs(new Blob([data]), `Feedback_Report.${format}`));
  },
  { serializeError }
);

export const downloadFeedbackReportSummaryPulseCombined = createAsyncThunk<
  void,
  { type: ReportFormatType }
>(
  `${STORE_NAME}/downloadFeedbackReportSummaryPulseCombined`,
  ({ type }, { getState }) => {
    const projectId = selectCurrentProjectId(getState());
    const format = toFileExtension(type);

    const filtersQuery = selectFiltersQuery(getState());

    const categoryMode = selectSummaryCategoryViewMode(getState());
    const language = selectLanguage(getState());
    const sortBy = selectSorting(getState());

    const query = queryDto.build(
      filtersQuery,
      queryDto.stringifyCategoryMode(categoryMode),
      queryDto.stringifyLanguage(language),
      queryDto.stringifySortBy(sortBy)
    );

    return reportApi
      .getFeedbackReportSummaryPulseCombined(projectId, query, type)
      .then((data) => FileSaver.saveAs(new Blob([data]), `Feedback_Report.${format}`));
  },
  { serializeError }
);

export const downloadFeedbackReportSummaryPulseCompared = createAsyncThunk<
  void,
  { type: ReportFormatType }
>(
  `${STORE_NAME}/downloadFeedbackReportSummaryPulseCompared`,
  ({ type }, { getState }) => {
    const projectId = selectCurrentProjectId(getState());
    const format = toFileExtension(type);

    const filtersQuery = selectFiltersQuery(getState());

    const categoryMode = selectSummaryCategoryViewMode(getState());
    const language = selectLanguage(getState());
    const sortBy = selectSorting(getState());

    const query = queryDto.build(
      filtersQuery,
      queryDto.stringifyCategoryMode(categoryMode),
      queryDto.stringifyLanguage(language),
      queryDto.stringifySortBy(sortBy)
    );

    return reportApi
      .getFeedbackReportSummaryPulseCompared(projectId, query, type)
      .then((data) => FileSaver.saveAs(new Blob([data]), `Feedback_Report.${format}`));
  },
  { serializeError }
);

export const downloadFeedbackPulseCombinedReport = createAsyncThunk<
  void,
  { type: ReportFormatType; departmentId?: number; departmentName?: string }
>(
  `${STORE_NAME}/downloadFeedbackPulseCombinedReport`,
  ({ type, departmentName, departmentId }, { getState }) => {
    const { projectId, format, query } = getFeedbackCombinedReportQuery(
      getState,
      type,
      departmentId
    );

    return reportApi
      .getFeedbackPulseCombinedReport(projectId, query, type)
      .then((data) =>
        FileSaver.saveAs(
          new Blob([data]),
          `${departmentName ? `${departmentName}_` : ''}All_Feedback_Report.${format}`
        )
      );
  },
  { serializeError }
);

export const downloadFeedbackReportOnDemand = createAsyncThunk<void, { type: ReportFormatType }>(
  `${STORE_NAME}/downloadFeedbackReportOnDemand`,
  ({ type }, { getState }) => {
    const isCompareMode = selectIsCompareMode(getState());
    const projectId = selectCurrentProjectId(getState());
    const format = toFileExtension(type);

    const filtersQuery = selectFiltersQuery(getState());

    const language = selectLanguage(getState());
    const sortBy = selectSorting(getState());

    const query = queryDto.build(
      filtersQuery,
      queryDto.stringifyLanguage(language),
      queryDto.stringifySortBy(sortBy)
    );

    const fetchReport = isCompareMode
      ? reportApi.getFeedbackReportOnDemandCompared(projectId, query, type)
      : reportApi.getFeedbackReportOnDemandCombined(projectId, query, type);

    return fetchReport.then((data) =>
      FileSaver.saveAs(new Blob([data]), `Feedback_Report.${format}`)
    );
  },
  { serializeError }
);

export const downloadFeedbackReportLifecycle = createAsyncThunk<void, { type: ReportFormatType }>(
  `${STORE_NAME}/downloadFeedbackReportLifecycle`,
  ({ type }, { getState }) => {
    const isCompareMode = selectIsCompareMode(getState());
    const projectId = selectCurrentProjectId(getState());
    const format = toFileExtension(type);

    const filtersQuery = selectLifecycleFiltersQuery(getState());

    const language = selectLanguage(getState());
    const sortBy = selectSorting(getState());

    const query = queryDto.build(
      filtersQuery,
      queryDto.stringifyLanguage(language),
      queryDto.stringifySortBy(sortBy)
    );

    const fetchReport = isCompareMode
      ? reportApi.getFeedbackReportLifecycleCompared(projectId, query, type)
      : reportApi.getFeedbackReportLifecycleCombined(projectId, query, type);

    return fetchReport.then((data) =>
      FileSaver.saveAs(new Blob([data]), `Feedback_Report.${format}`)
    );
  },
  { serializeError }
);

export const downloadFeedbackCombinedReportOnDemand = createAsyncThunk<
  void,
  { type: ReportFormatType; departmentId?: number; departmentName?: string }
>(
  `${STORE_NAME}/downloadFeedbackCombinedReportOnDemand`,
  ({ type, departmentId, departmentName }, { getState }) => {
    const { projectId, format, query } = getFeedbackCombinedReportQuery(
      getState,
      type,
      departmentId
    );

    return reportApi
      .getFeedbackCombinedReportOnDemand(projectId, query, type)
      .then((data) =>
        FileSaver.saveAs(
          new Blob([data]),
          `${departmentName ? `${departmentName}_` : ''}All_Feedback_Report.${format}`
        )
      );
  },
  { serializeError }
);

export const downloadFeedbackCombinedReportOverallScore = createAsyncThunk<
  void,
  { type: ReportFormatType; departmentId?: number; departmentName?: string }
>(
  `${STORE_NAME}/downloadFeedbackCombinedReportOverallScore`,
  ({ type, departmentId, departmentName }, { getState }) => {
    const { projectId, format, query } = getFeedbackCombinedReportQuery(
      getState,
      type,
      departmentId
    );

    return reportApi
      .getFeedbackCombinedReportOverallScore(projectId, query, type)
      .then((data) =>
        FileSaver.saveAs(
          new Blob([data]),
          `${departmentName ? `${departmentName}_` : ''}All_Feedback_Report.${format}`
        )
      );
  },
  { serializeError }
);

export const downloadFeedbackCombinedReportEngagementScore = createAsyncThunk<
  void,
  { type: ReportFormatType; departmentId?: number; departmentName?: string }
>(
  `${STORE_NAME}/downloadFeedbackCombinedReportEngagementScore`,
  ({ type, departmentId, departmentName }, { getState }) => {
    const { projectId, format, query } = getFeedbackCombinedReportQuery(
      getState,
      type,
      departmentId
    );

    return reportApi
      .getFeedbackCombinedReportEngagementScore(projectId, query, type)
      .then((data) =>
        FileSaver.saveAs(
          new Blob([data]),
          `${departmentName ? `${departmentName}_` : ''}All_Feedback_Report.${format}`
        )
      );
  },
  { serializeError }
);

export const downloadFeedbackCombinedReportCASScore = createAsyncThunk<
  void,
  { type: ReportFormatType; departmentId?: number; departmentName?: string }
>(
  `${STORE_NAME}/downloadFeedbackCombinedReportCASScore`,
  ({ type, departmentId, departmentName }, { getState }) => {
    const { projectId, format, query } = getFeedbackCombinedReportQuery(
      getState,
      type,
      departmentId
    );

    return reportApi
      .getFeedbackCombinedReportCASScore(projectId, query, type)
      .then((data) =>
        FileSaver.saveAs(
          new Blob([data]),
          `${departmentName ? `${departmentName}_` : ''}All_Feedback_Report.${format}`
        )
      );
  },
  { serializeError }
);

export const downloadCategoryReport = createAsyncThunk<void, { type: ReportFormatType }>(
  `${STORE_NAME}/downloadCategoryReport`,
  ({ type }, { getState }) => {
    const projectId = selectCurrentProjectId(getState());
    const detailsByCategoryId = selectDetailsByCategoryId(getState());
    const categoryId = detailsByCategoryId?.category?.id;
    const filtersQuery = selectFiltersQuery(getState());

    const format = toFileExtension(type);
    const mode = selectIsCompareMode(getState()) ? ModeType.COMPARED : ModeType.COMBINED;
    const language = selectLanguage(getState());
    const sortBy = selectSorting(getState());

    const query = queryDto.build(
      filtersQuery,
      queryDto.stringifyLanguage(language),
      queryDto.stringifySortBy(sortBy)
    );

    if (categoryId) {
      return reportApi
        .getCategoryReport(projectId, categoryId, query, mode, type)
        .then((data) => FileSaver.saveAs(new Blob([data]), `Feedback_Report.${format}`));
    }
  },
  { serializeError }
);

export const downloadCategoryReportSummaryPulseCombined = createAsyncThunk<
  void,
  { type: ReportFormatType }
>(
  `${STORE_NAME}/downloadCategoryReportSummaryPulseCombined`,
  ({ type }, { getState }) => {
    const projectId = selectCurrentProjectId(getState());

    const currentCategoryId = selectCurrentCategoryId(getState());
    const reportCategoryId = selectReportCategoryId(getState());
    const categoryId = currentCategoryId || reportCategoryId;

    const filtersQuery = selectFiltersQuery(getState());

    const format = toFileExtension(type);
    const language = selectLanguage(getState());
    const sortBy = selectSorting(getState());

    const query = queryDto.build(
      filtersQuery,
      queryDto.stringifyLanguage(language),
      queryDto.stringifySortBy(sortBy)
    );

    if (categoryId) {
      return reportApi
        .getCategoryReportSummaryPulseCombined(projectId, categoryId, query, type)
        .then((data) => FileSaver.saveAs(new Blob([data]), `Feedback_Report.${format}`));
    }
  },
  { serializeError }
);

export const downloadCategoryReportSummaryPulseCompared = createAsyncThunk<
  void,
  { type: ReportFormatType }
>(
  `${STORE_NAME}/downloadCategoryReportSummaryPulseCompared`,
  ({ type }, { getState }) => {
    const projectId = selectCurrentProjectId(getState());

    const currentCategoryId = selectCurrentCategoryId(getState());
    const reportCategoryId = selectReportCategoryId(getState());
    const categoryId = currentCategoryId || reportCategoryId;

    const filtersQuery = selectFiltersQuery(getState());

    const format = toFileExtension(type);
    const language = selectLanguage(getState());
    const sortBy = selectSorting(getState());

    const query = queryDto.build(
      filtersQuery,
      queryDto.stringifyLanguage(language),
      queryDto.stringifySortBy(sortBy)
    );

    if (categoryId) {
      return reportApi
        .getCategoryReportSummaryPulseCompared(projectId, categoryId, query, type)
        .then((data) => FileSaver.saveAs(new Blob([data]), `Feedback_Report.${format}`));
    }
  },
  { serializeError }
);

export const downloadCommentReport = createAsyncThunk<
  void,
  { type: ReportFormatType; questionName?: string }
>(
  `${STORE_NAME}/downloadCommentReport`,
  ({ type, questionName }, { getState }) => {
    const projectId = selectCurrentProjectId(getState());
    const questionId = selectSelectedQuestionId(getState());

    if (!questionId) return;

    const filtersQueryString = selectCommentsPageFilterQueryString(getState());
    const language = selectLanguage(getState());
    const sortBy = selectSorting(getState());

    const query = queryDto.build(
      filtersQueryString,
      queryDto.stringifyFormat(type),
      queryDto.stringifyLanguage(language),
      queryDto.stringifySortBy(sortBy)
    );

    const format = toFileExtension(type);
    const fileName = questionName
      ? `Comment_Report_${questionName}.${format}`
      : `Comment_Report.${format}`;

    return reportApi
      .getCommentReport(projectId, questionId, query)
      .then((data) => FileSaver.saveAs(new Blob([data]), fileName));
  },
  { serializeError }
);

export const downloadInsightsReport = createAsyncThunk<void, { type: ReportFormatType }>(
  `${STORE_NAME}/downloadInsightsReport`,
  async ({ type }, { getState }) => {
    const projectId = selectCurrentProjectId(getState());
    const isLifecycleKind = selectIsLifecycleProjectKind(getState());
    const format = toFileExtension(type);
    const isCompareMode = selectIsCompareMode(getState());

    const filtersQuery = isLifecycleKind
      ? selectLifecycleFiltersQuery(getState())
      : selectFiltersQuery(getState());

    const language = selectLanguage(getState());
    const sortBy = selectSorting(getState());

    const query = queryDto.build(
      filtersQuery,
      queryDto.stringifyLanguage(language),
      queryDto.stringifySortBy(sortBy)
    );

    let reportApiMethod;
    if (!isCompareMode && !isLifecycleKind) {
      reportApiMethod = reportApi.getInsightsCombinedReport.bind(reportApi);
    } else if (!isCompareMode && isLifecycleKind) {
      reportApiMethod = reportApi.getInsightsLifecycleCombinedReport.bind(reportApi);
    } else if (isCompareMode && isLifecycleKind) {
      reportApiMethod = reportApi.getInsightsLifecycleComparedReport.bind(reportApi);
    } else {
      reportApiMethod = reportApi.getInsightsComparedReport.bind(reportApi);
    }

    const data = await reportApiMethod(projectId, query, type);
    FileSaver.saveAs(new Blob([data]), `Insights_Report.${format}`);
  },
  { serializeError }
);

export const downloadAllCommentsExcelReport = createAsyncThunk(
  `${STORE_NAME}/downloadAllCommentsExcelReport`,
  (_, { getState }) => {
    const projectId = selectCurrentProjectId(getState());
    const commentsPageFilterQueryString = selectCommentsPageFilterQueryString(getState());

    const query = queryDto.build(commentsPageFilterQueryString);

    return reportApi
      .getAllCommentsReport(projectId, query, ReportFormat.Excel)
      .then((data) =>
        FileSaver.saveAs(new Blob([data]), `All_Comments_Report.${ReportFileExtension.Excel}`)
      );
  },
  { serializeError }
);

export const downloadAllCommentsPdfReport = createAsyncThunk<
  void,
  { departmentId?: number; departmentName?: string }
>(
  `${STORE_NAME}/downloadAllCommentsPdfReport`,
  ({ departmentId, departmentName }, { getState }) => {
    const projectId = selectCurrentProjectId(getState());
    const commentsPageFilterQueryString = selectCommentsPageFilterQueryString(getState());
    const language = selectLanguage(getState());
    const sortBy = selectSorting(getState());

    const query = queryDto.build(
      commentsPageFilterQueryString,
      queryDto.stringifyLanguage(language),
      queryDto.stringifySortBy(sortBy),
      queryDto.stringifyFilter({ [QueryType.Department]: [departmentId] })
    );

    return reportApi
      .getAllCommentsReport(projectId, query, ReportFormat.PDF)
      .then((data) =>
        FileSaver.saveAs(
          new Blob([data]),
          `${departmentName ? `${departmentName}_` : ''}All_Comments_Report.${
            ReportFileExtension.PDF
          }`
        )
      );
  },
  { serializeError }
);

export const downloadMonthlyBreakdownReport = createAsyncThunk<
  void,
  { monthlyBreakdownReport: MonthlyBreakdownReportType }
>(
  `${STORE_NAME}/downloadMonthlyBreakdownReport`,
  ({ monthlyBreakdownReport }, { getState }) => {
    const projectId = selectCurrentProjectId(getState());
    const query = selectFiltersQuery(getState());

    return reportApi
      .getMonthlyBreakdownReport(projectId, query, monthlyBreakdownReport)
      .then((data) =>
        FileSaver.saveAs(new Blob([data]), `Monthly_Breakdown.${ReportFileExtension.Excel}`)
      );
  },
  { serializeError }
);

export const downloadRespondentIndividualFeedbackReport = createAsyncThunk<
  void,
  { type: ReportFormatType; projectId: number; respondentIds: string[] }
>(
  `${STORE_NAME}/downloadRespondentIndividualFeedbackReport`,
  ({ type, projectId, respondentIds }, { getState }) => {
    const format = toFileExtension(type);
    const fileName = `Respondent_Individual_Feedback_Report_${respondentIds?.[0]}.${format}`;
    const language = selectLanguage(getState());
    const sortBy = selectSorting(getState());

    return reportApi
      .getRespondentIndividualFeedbackReport({ type, projectId, respondentIds, language, sortBy })
      .then((data) => FileSaver.saveAs(new Blob([data]), fileName));
  },
  { serializeError }
);

export const downloadIndividualFeedbackReport = createAsyncThunk<
  void,
  {
    type: ReportFormatType;
    projectId: number;
    advocacyFilter: AdvocacyDto[];
    classifications: GroupedClassifications;
    departments: number[];
    respondentLikeFilter: string;
  }
>(
  `${STORE_NAME}/downloadIndividualFeedbackReport`,
  (props, { getState }) => {
    const format = toFileExtension(props.type);
    const fileName = `Individual_Feedbacks_Report.${format}`;
    const language = selectLanguage(getState());
    const sortBy = selectSorting(getState());

    return reportApi
      .getIndividualFeedbackReport({ ...props, language, sortBy })
      .then((data) => FileSaver.saveAs(new Blob([data]), fileName));
  },
  { serializeError }
);
